import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft, MessageCircle, ScrollText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CheckerBoard from './CheckerBoard';
import { toast } from 'sonner';
import { useGame } from '@/hooks/use-game';
import { useRooms } from '@/hooks/use-rooms';
import { supabase } from '@/integrations/supabase/client';
import maskSensitiveInfo from '@/integrations/supabase/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { useSession } from '@supabase/auth-helpers-react';
import { useUserProfile } from '@/hooks/use-user-profile';
import { gameSounds } from '@/lib/sounds';
import { GameChat } from './GameChat';
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { generateGradientAvatar } from '@/lib/generate-avatar';
import { GameHistory } from './GameHistory';
import { PlayerProfileCard } from './PlayerProfileCard';
import { useAnalytics } from '@/hooks/use-analytics';
import { useMoveHistory } from '@/hooks/use-move-history';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  calculateValidMoves as getValidMoves,
  cloneBoard,
  countPieces,
  createInitialBoard,
  findMandatoryPieces as getMandatoryPieces,
  hasTwoPlayers,
  isKing,
  parseBoard,
  type BoardPosition,
  type ValidMove,
} from '@/lib/game';

interface GameRoomProps {
  roomId: string;
  playerName: string;
  isGuest?: boolean;
}

const GameRoom = ({ roomId, playerName, isGuest = false }: GameRoomProps) => {
  const navigate = useNavigate();
  const { leaveRoom } = useRooms();
  const session = useSession();
  const {
    game,
    loading,
    error,
    joinGame,
    makeMove,
    isPlayer1,
    isPlayer2,
    canPlay,
    refetch,
    isCurrentPlayerPiece,
    canJoinGame
  } = useGame(roomId);

  const isMobile = useIsMobile();
  const [isMoving, setIsMoving] = useState(false);

  const { profile: player1Profile } = useUserProfile(game?.player1_id);
  const { profile: player2Profile } = useUserProfile(game?.player2_id);

  const [player1Avatar, setPlayer1Avatar] = useState<string | null>(null);
  const [player2Avatar, setPlayer2Avatar] = useState<string | null>(null);

  // Mover a função hasTwoPlayers para aqui, antes do seu uso
  const getDisplayName = (playerId: string | null, playerName: string | null) => {
    if (!playerId && !playerName) return 'Aguardando jogador';

    // Se for um nome de convidado, retornar direto
    if (playerName && (!playerId || playerId === 'guest')) {
      return playerName;
    }

    // Se tiver um perfil, usar as informações do perfil
    const profile = playerId === game?.player1_id ? player1Profile : player2Profile;
    if (profile) {
      return profile.username ? `@${profile.username}` : profile.display_name || playerName;
    }

    // Se não tiver perfil, usar o nome fornecido
    return playerName || 'Jogador';
  };

  // Atualizar a verificação de início de jogo
  const gameStarted = hasTwoPlayers(game);

  // Atualizar a verificação de turno
  const isMyTurn = game ? (
    (isPlayer1 && game.current_player === 1) ||
    (isPlayer2 && game.current_player === 2)
  ) && gameStarted : false;

  // Atualizar a lógica de carregamento de avatar
  const getPlayerAvatar = useCallback(async (playerId: string | null, playerName: string | null) => {
    if (!playerId && !playerName) return null;

    // Se for convidado, gerar avatar baseado no nome
    if (!playerId || playerId === 'guest') {
      return generateGradientAvatar(playerName || '?');
    }

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', playerId)
        .single();

      if (profile?.equipped_skin_id) {
        const { data: skin } = await supabase
          .from('avatar_skins')
          .select('image_url')
          .eq('id', profile.equipped_skin_id)
          .single();

        if (skin?.image_url) return skin.image_url;
      }

      if (profile?.avatar_url) return profile.avatar_url;
      if (profile?.display_name) return generateGradientAvatar(profile.display_name);
      if (profile?.username) return generateGradientAvatar(profile.username);
      return generateGradientAvatar(playerName || '?');
    } catch (error) {
      console.error('Erro ao carregar avatar:', error);
      return generateGradientAvatar(playerName || '?');
    }
  }, []);

  // Atualizar useEffect para carregar avatares
  useEffect(() => {
    if (!game) return;

    const loadAvatars = async () => {
      try {
        const avatar1 = await getPlayerAvatar(game.player1_id, game.player1_name);
        const avatar2 = await getPlayerAvatar(game.player2_id, game.player2_name);

        setPlayer1Avatar(avatar1);
        setPlayer2Avatar(avatar2);
      } catch (err) {
        console.error('Erro ao carregar avatares:', err);
      }
    };

    loadAvatars();
  }, [game, getPlayerAvatar]);

  const [selectedPiece, setSelectedPiece] = useState<BoardPosition | null>(null);
  const [validMoves, setValidMoves] = useState<ValidMove[]>([]);
  const [mandatoryPieces, setMandatoryPieces] = useState<BoardPosition[]>([]);
  const [mustContinueCapturing, setMustContinueCapturing] = useState<BoardPosition | null>(null);

  const { moveHistory, resetMoveHistory } = useMoveHistory(game?.id, refetch);

  const previousGameRef = useRef<typeof game>(null);

  useEffect(() => {
    const previousGame = previousGameRef.current;

    if (previousGame && game && previousGame.id === game.id) {
      const opponentLeft = hasTwoPlayers(previousGame) && !hasTwoPlayers(game) && (isPlayer1 || isPlayer2);

      if (opponentLeft) {
        toast.info('O adversário saiu da sala. Aguardando novo jogador...');
        setSelectedPiece(null);
        setValidMoves([]);
        setMandatoryPieces([]);
        setMustContinueCapturing(null);
      }
    }

    previousGameRef.current = game;
  }, [game, isPlayer1, isPlayer2]);

  // Função para registrar movimentos no histórico
  // Converter o board de Json para number[][]
  const getBoard = () => parseBoard(game?.board);

  const handleLeaveGame = async () => {
    try {
      console.log('Saindo da sala...');

      if (!game) return;

      await leaveRoom(roomId);
      navigate('/');
    } catch (err) {
      console.error('Erro ao sair da sala:', err);
      toast.error('Erro ao sair da sala');
    }
  };

  // Adicionar useEffect para tocar sons de vitória/derrota
  useEffect(() => {
    if (game?.winner_id) {
      const isWinner = game.winner_id === (isPlayer1 ? game.player1_id : game.player2_id);
      gameSounds.play(isWinner ? 'victory' : 'defeat');
    }
  }, [game?.winner_id, isPlayer1, game?.player1_id, game?.player2_id]);

  const handleSquareClick = async (row: number, col: number) => {
    if (!game) return;

    // Verificar se tem dois jogadores antes de qualquer outra verificação
    if (!hasTwoPlayers(game)) {
      gameSounds.play('error');
      toast.error("Aguardando outro jogador para iniciar a partida!");
      return;
    }

    // Verificar se o jogo está ativo
    if (!canPlay || isMoving) {
      return;
    }

    const isMyTurn = (isPlayer1 && game.current_player === 1) ||
                    (isPlayer2 && game.current_player === 2);

    // Verificar turno antes de qualquer ação
    if (!isMyTurn && !mustContinueCapturing) {
      gameSounds.play('error');
      toast.error("Não é sua vez de jogar!");
      return;
    }

    // Ao selecionar uma peça
    if (isCurrentPlayerPiece(row, col)) {
      // Verificar restrições de captura obrigatória
      if (mustContinueCapturing && (row !== mustContinueCapturing.row || col !== mustContinueCapturing.col)) {
        gameSounds.play('error');
        toast.error("Você deve continuar capturando com a mesma peça!");
        return;
      }

      // Verificar capturas obrigatórias disponíveis
      if (!mustContinueCapturing) {
        const mandatoryPieces = getMandatoryPieces(getBoard(), game.current_player);
        if (mandatoryPieces.length > 0 && !mandatoryPieces.some(p => p.row === row && p.col === col)) {
          gameSounds.play('error');
          setMandatoryPieces(mandatoryPieces);
          toast.error("Você tem capturas obrigatórias disponíveis!");
          return;
        }
      }

      // Seleção válida de peça
      setMandatoryPieces([]);
      setSelectedPiece({ row, col });
      setValidMoves(getValidMoves(getBoard(), row, col, {
        currentPlayer: game.current_player,
        onlyCaptures: !!mustContinueCapturing,
      }));
      return;
    }

    // Ao fazer um movimento
    if (selectedPiece) {
      const validMove = validMoves.find(move => move.row === row && move.col === col);

      if (validMove) {
        let keepCaptureSelection = false;

        try {
          setIsMoving(true);
          const board = getBoard();
          const selectedPieceType = board[selectedPiece.row][selectedPiece.col];
          const isAlreadyKing = isKing(selectedPieceType);

          const isPromotion =
            !isAlreadyKing &&
            ((game.current_player === 1 && row === 7) ||
             (game.current_player === 2 && row === 0));

          if (validMove.isCapture) {
            await makeMove(
              selectedPiece.row,
              selectedPiece.col,
              row,
              col,
              true,
              isPromotion,
              validMove.capturedRow,
              validMove.capturedCol,
              false
            );

            gameSounds.play('capture');

            // Rastrear captura
            analytics.trackCapture(
              game.id,
              moveHistory.length + 1,
              isAlreadyKing ? 'dama' : 'normal'
            );

            const nextBoard = cloneBoard(board);
            nextBoard[row][col] = isPromotion ? (game.current_player === 1 ? 3 : 4) : selectedPieceType;
            nextBoard[selectedPiece.row][selectedPiece.col] = 0;
            if (validMove.capturedRow !== undefined && validMove.capturedCol !== undefined) {
              nextBoard[validMove.capturedRow][validMove.capturedCol] = 0;
            }

            const nextCaptureMoves = getValidMoves(nextBoard, row, col, {
              currentPlayer: game.current_player,
              onlyCaptures: true,
            });
            const hasMoreCaptures = nextCaptureMoves.some(move => move.isCapture);

            if (hasMoreCaptures) {
              keepCaptureSelection = true;
              setMustContinueCapturing({ row, col });
              setSelectedPiece({ row, col });
              setValidMoves(nextCaptureMoves);
              setMandatoryPieces([]);
              toast.info("Você deve continuar capturando!");
              return;
            }
          } else {
            await makeMove(
              selectedPiece.row,
              selectedPiece.col,
              row,
              col,
              false,
              isPromotion
            );

            gameSounds.play('move');
          }

          if (isPromotion) {
            setTimeout(() => {
              gameSounds.play('promotion');
              // Rastrear promoção
              analytics.trackPromotion(game.id, moveHistory.length + 1);
            }, 300);
          }

          // Rastrear movimento normal
          analytics.trackMove(game.id, moveHistory.length + 1);
        } catch (error) {
          console.error('Erro ao fazer movimento:', maskSensitiveInfo(error));
          toast.error('Erro ao fazer movimento');
          // Rastrear erro
          analytics.trackError(
            game.id,
            'movimento_invalido',
            'Erro ao fazer movimento'
          );
        } finally {
          setIsMoving(false);
          if (!keepCaptureSelection) {
            setSelectedPiece(null);
            setValidMoves([]);
            setMustContinueCapturing(null);
          }
        }
      }
    }
  };

  const handlePlayAgain = async () => {
    if (!game?.id) return;

    const initialBoard = createInitialBoard();

    try {
      // Limpar o histórico de movimentos
      await supabase
        .from('moves')
        .delete()
        .eq('game_id', game.id);

      // Atualizar o jogo
      const { error } = await supabase
        .from('games')
        .update({
          board: initialBoard,
          current_player: 1,
          winner_id: null,
          status: hasTwoPlayers(game) ? 'playing' : 'waiting',
          updated_at: new Date().toISOString()
        })
        .eq('room_id', roomId);

      if (error) throw error;

      // Resetar o estado local do histórico
      resetMoveHistory();

      await refetch();
      toast.success('Novo jogo iniciado!');
    } catch (err) {
      console.error('Erro ao reiniciar jogo:', err);
      toast.error('Erro ao reiniciar jogo');
    }
  };

  // Função para reiniciar o jogo
  const handleRestartGame = useCallback(async () => {
    if (!game || !session?.user) return;

    try {
      const { error } = await supabase
        .from('games')
        .update({
          board: createInitialBoard(),
          current_player: 1,
          winner_id: null,
          status: hasTwoPlayers(game) ? 'playing' : 'waiting',
          last_move_time: new Date().toISOString()
        })
        .eq('room_id', roomId);

      if (error) throw error;
      toast.success('Jogo reiniciado!');
    } catch (err) {
      console.error('Erro ao reiniciar jogo:', err);
      toast.error('Erro ao reiniciar o jogo');
    }
  }, [game, roomId, session?.user]);

  // Adicionar useEffect para mostrar diálogo quando um novo jogador entrar
  useEffect(() => {
    if (!game || !session?.user) return;

    // Se um novo jogador entrou e existe um jogador que já estava na sala
    const wasWaitingForPlayer = !game.player2_id && game.player1_id && game.player1_id !== session.user.id;
    if (wasWaitingForPlayer && game.player2_id) {
      // Perguntar ao jogador 1 se quer reiniciar
      const dialog = confirm('Um novo jogador entrou! Deseja reiniciar o jogo?');
      if (dialog) {
        handleRestartGame();
      }
    }
  }, [game, handleRestartGame, session?.user]);

  // Adicionar estado para mensagem de status
  const [statusMessage, setStatusMessage] = useState<string>('');

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const { width, height } = container.getBoundingClientRect();

      const x = (clientX - width / 2) / width;
      const y = (clientY - height / 2) / height;

      container.style.setProperty('--mouse-x', x.toString());
      container.style.setProperty('--mouse-y', y.toString());
    };

    container.addEventListener('mousemove', handleMouseMove);
    return () => container.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleGameEnd = useCallback(async (winnerId: string | null, loserId: string | null) => {
    if (!game?.id || !session?.user?.id) return;

    // Garantir que apenas o vencedor atualize suas próprias estatísticas
    if (winnerId !== session.user.id) {
      return;
    }

    try {
      // Atualizar apenas as estatísticas gerais
      await supabase.rpc('update_game_statistics', {
        winner_id: winnerId,
        loser_id: loserId || '00000000-0000-0000-0000-000000000000'
      });

      toast.success('Jogo finalizado! As estatísticas foram atualizadas.');
      refetch();
    } catch (error) {
      console.error('Erro ao atualizar estatísticas:', error);
      toast.error('Erro ao atualizar estatísticas do jogo');
    }
  }, [game?.id, refetch, session?.user?.id]);

  // Atualizar useEffect para considerar jogos com convidados
  useEffect(() => {
    if (!game?.id || !game.winner_id || !session?.user?.id) return;

    const updateGameStats = async () => {
      const loserId = game.winner_id === game.player1_id ? game.player2_id : game.player1_id;
      await handleGameEnd(game.winner_id, loserId);
    };

    updateGameStats();
  }, [game?.id, game?.winner_id, game?.player1_id, game?.player2_id, handleGameEnd, session?.user?.id]);

  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);

  // Adicionar função para obter texto do status
  const getStatusText = () => {
    if (!game) return 'Carregando...';
    if (game.winner_id || game.status === 'finished') return 'Partida finalizada';
    if (!hasTwoPlayers(game)) return 'Aguardando jogador';
    return 'Partida em andamento';
  };

  // Adicionar função para obter cor do status
  const getStatusColor = () => {
    if (!game) return 'text-gray-400';
    if (game.winner_id || game.status === 'finished') return 'text-blue-400';
    if (!hasTwoPlayers(game)) return 'text-yellow-400';
    return 'text-green-400';
  };

  const analytics = useAnalytics();

  useEffect(() => {
    const gameId = game?.id;
    if (!gameId) return;

    const startTime = Date.now(); // Marca o tempo inicial quando o jogo começa
    analytics.trackGameStart(gameId);

    return () => {
      const duration = Math.floor((Date.now() - startTime) / 1000); // Calcula quantos segundos se passaram
      analytics.trackGameEnd(gameId, 'victory', duration);
    };
  }, [game?.id, analytics]);

  useEffect(() => {
    if (game?.winner_id && game?.id) {
      const result = game.winner_id === (isPlayer1 ? game.player1_id : game.player2_id)
        ? 'victory'
        : 'defeat';

      analytics.trackGameEnd(game.id, result, 0); // Aqui poderíamos implementar o cálculo da duração real do jogo
    }
  }, [game?.winner_id, game?.id, isPlayer1, game?.player1_id, game?.player2_id, analytics]);

  // Função para rastrear movimentos
  const trackMove = useCallback((fromRow: number, fromCol: number, toRow: number, toCol: number) => {
    if (game?.id) {
      analytics.trackMove(game.id, moveHistory.length + 1);
    }
  }, [game?.id, moveHistory.length, analytics]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#000510] to-[#001233]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-900 to-zinc-800">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-bold text-red-500 mb-4">Erro</h2>
          <p className="text-gray-400">{error}</p>
          <Button
            onClick={() => navigate('/rooms')}
            className="mt-4 bg-zinc-800 hover:bg-zinc-700"
          >
            Voltar para Salas
          </Button>
        </Card>
      </div>
    );
  }

  if (!game) return null;

  const player1Name = isPlayer1
    ? (isGuest ? playerName : getDisplayName(session?.user?.id, session?.user?.email))
    : getDisplayName(game?.player1_id, game?.player1_name);

  const player2Name = isPlayer2
    ? (isGuest ? playerName : getDisplayName(session?.user?.id, session?.user?.email))
    : getDisplayName(game?.player2_id, game?.player2_name);

  const board = getBoard();

  // Adicionar cálculo de peças
  const pieceCounts = countPieces(board);
  const player1Pieces = pieceCounts.player1;
  const player2Pieces = pieceCounts.player2;

  // Remover restrições de visualização de perfil para convidados
  const handlePlayerClick = (playerId: string | null, playerName: string | null) => {
    if (playerId) {
      setSelectedPlayer(playerId);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative min-h-screen overflow-hidden nebula-bg"
    >
      {/* Círculos gradientes flutuantes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className={cn(
              "absolute w-96 h-96 rounded-full blur-3xl opacity-20",
              i === 0 && "-top-48 -left-48 bg-blue-500",
              i === 1 && "top-1/2 -right-48 bg-purple-500",
              i === 2 && "bottom-0 left-1/2 bg-red-500"
            )}
          />
        ))}
      </div>

      {/* Partículas brilhantes */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-blue-400 rounded-full animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Overlay com gradiente suave */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle at calc(50% + calc(var(--mouse-x, 0) * 30%)) calc(50% + calc(var(--mouse-y, 0) * 30%)), transparent 0%, rgba(0, 18, 51, 0.3) 100%)`,
          transition: 'background 0.2s ease-out'
        }}
      />

      {/* Conteúdo principal */}
      <div className="relative z-10 min-h-[100dvh]">
        <div className="min-h-[100dvh] flex items-start justify-center p-0 md:p-4">
          <Card className="w-full max-w-[1600px] min-h-[100dvh] md:min-h-[600px] md:h-[calc(100vh-32px)] p-0 md:p-2 bg-zinc-900/40 backdrop-blur-md border-zinc-800/50 flex flex-col rounded-none md:rounded-lg md:mt-0">
            {/* Header com botão de sair */}
            <div className="sticky top-0 z-20 flex items-center gap-2 min-h-14 px-3 py-2 bg-zinc-950/80 backdrop-blur-md md:bg-zinc-900/50 md:mb-2 md:rounded-md border-b md:border border-white/5">
              <Button
                variant="outline"
                className="bg-zinc-800/60 text-white hover:bg-zinc-700/60 h-10 md:h-8 px-3 md:px-2 flex items-center shrink-0 border-zinc-700/50"
                onClick={handleLeaveGame}
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="ml-1 text-sm font-medium">Sair</span>
              </Button>

              <div className="flex-1 text-center min-w-0">
                <h1 className="text-sm md:text-base font-bold text-white/90 truncate">
                  Sala: {roomId}
                </h1>
                <p className={cn("text-[11px] md:text-xs font-medium truncate", getStatusColor())}>
                  {statusMessage || getStatusText()}
                </p>
              </div>

              <div className="w-[76px] md:w-[72px] shrink-0"></div>
            </div>

            {/* Layout principal */}
            <div className="flex-1 flex flex-col md:flex-row gap-3 md:gap-4 overflow-y-auto md:overflow-hidden items-start md:items-center justify-start md:justify-center p-2 pb-[calc(env(safe-area-inset-bottom)+16px)] md:pb-2 bg-zinc-900/30 md:rounded-lg border-0 md:border border-white/5">
              {/* Coluna da esquerda - Avatares Desktop */}
              <div className="hidden md:flex w-[15%] max-w-[140px] min-w-[120px] flex-shrink-0 flex-col justify-between h-[calc(75vh_-_32px)]">
                {/* Avatar do oponente (topo) */}
                <div className="flex flex-col items-center">
                  <Avatar
                    className={cn(
                      "w-24 h-24 ring-2 cursor-pointer hover:ring-4 transition-all",
                      isPlayer2
                        ? "ring-red-500 shadow-lg shadow-red-500/20"
                        : "ring-blue-500 shadow-lg shadow-blue-500/20"
                    )}
                    onClick={() => handlePlayerClick(
                      isPlayer2 ? game?.player1_id : game?.player2_id,
                      isPlayer2 ? game?.player1_name : game?.player2_name
                    )}
                  >
                    <AvatarImage
                      src={isPlayer2 ? player1Avatar : player2Avatar}
                      alt={isPlayer2 ? player1Name : player2Name}
                    />
                    <AvatarFallback className={cn(
                      "text-xl",
                      isPlayer2
                        ? "bg-gradient-to-br from-red-600 to-red-700"
                        : "bg-gradient-to-br from-blue-600 to-blue-700"
                    )}>
                      {(isPlayer2 ? player1Name : player2Name)?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-center mt-2">
                    <div className="font-bold text-sm text-white">
                      {isPlayer2 ? player1Name : player2Name}
                    </div>
                    <div className={cn(
                      "text-xs font-medium text-red-400",
                      isPlayer2 ? "text-red-400" : "text-blue-400"
                    )}>
                      Peças {isPlayer2 ? "Vermelhas" : "Azuis"}
                    </div>
                  </div>
                </div>

                {/* Avatar do jogador (base) */}
                <div className="flex flex-col items-center">
                  <Avatar
                    className={cn(
                      "w-24 h-24 ring-2 cursor-pointer hover:ring-4 transition-all",
                      isPlayer2
                        ? "ring-blue-500 shadow-lg shadow-blue-500/20"
                        : "ring-red-500 shadow-lg shadow-red-500/20"
                    )}
                    onClick={() => handlePlayerClick(
                      isPlayer2 ? game?.player2_id : game?.player1_id,
                      isPlayer2 ? game?.player2_name : game?.player1_name
                    )}
                  >
                    <AvatarImage
                      src={isPlayer2 ? player2Avatar : player1Avatar}
                      alt={isPlayer2 ? player2Name : player1Name}
                    />
                    <AvatarFallback className={cn(
                      "text-xl",
                      isPlayer2
                        ? "bg-gradient-to-br from-blue-600 to-blue-700"
                        : "bg-gradient-to-br from-red-600 to-red-700"
                    )}>
                      {(isPlayer2 ? player2Name : player1Name)?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-center mt-2">
                    <div className="font-bold text-sm text-white">
                      {isPlayer2 ? player2Name : player1Name}
                    </div>
                    <div className={cn(
                      "text-xs font-medium",
                      isPlayer2 ? "text-blue-400" : "text-red-400"
                    )}>
                      Peças {isPlayer2 ? "Azuis" : "Vermelhas"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Área central */}
              <div className="flex-1 flex flex-col items-center w-full md:max-w-[min(75vh,750px)]">
                {/* Avatares versão mobile */}
                <div className="grid md:hidden grid-cols-[1fr_auto_1fr] items-center gap-2 w-full rounded-lg border border-white/10 bg-black/25 p-2 mb-2">
                  {/* Avatar do oponente */}
                  <div className="min-w-0 flex items-center gap-2 rounded-md bg-white/[0.03] px-2 py-1.5">
                    <Avatar className={cn(
                      "w-9 h-9 ring-2 shrink-0",
                      isPlayer2
                        ? "ring-red-500"
                        : "ring-blue-500"
                    )}>
                      <AvatarImage
                        src={isPlayer2 ? player1Avatar : player2Avatar}
                        alt={isPlayer2 ? player1Name : player2Name}
                      />
                      <AvatarFallback>{(isPlayer2 ? player1Name : player2Name)?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="font-semibold text-white text-xs truncate">
                        {isPlayer2 ? player1Name : player2Name}
                      </div>
                      <div className={cn(
                        "text-[10px] font-medium",
                        isPlayer2 ? "text-red-400" : "text-blue-400"
                      )}>
                        {isPlayer2 ? "Vermelhas" : "Azuis"}
                      </div>
                    </div>
                  </div>

                  <div className={cn(
                    "h-9 min-w-14 rounded-full border px-2 flex flex-col items-center justify-center text-[10px] font-semibold",
                    isMyTurn
                      ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300"
                      : "border-zinc-600/40 bg-zinc-900/50 text-zinc-400"
                  )}>
                    <span>{isMyTurn ? "Sua vez" : "Turno"}</span>
                    <span className="text-[9px] opacity-80">{game.current_player === 1 ? "Verm." : "Azul"}</span>
                  </div>

                  {/* Avatar do jogador */}
                  <div className="min-w-0 flex items-center justify-end gap-2 rounded-md bg-white/[0.03] px-2 py-1.5">
                    <div className="min-w-0 text-right">
                      <div className="font-semibold text-white text-xs truncate">
                        {isPlayer2 ? player2Name : player1Name}
                      </div>
                      <div className={cn(
                        "text-[10px] font-medium",
                        isPlayer2 ? "text-blue-400" : "text-red-400"
                      )}>
                        {isPlayer2 ? "Azuis" : "Vermelhas"}
                      </div>
                    </div>
                    <Avatar className={cn(
                      "w-9 h-9 ring-2 shrink-0",
                      isPlayer2
                        ? "ring-blue-500"
                        : "ring-red-500"
                    )}>
                      <AvatarImage
                        src={isPlayer2 ? player2Avatar : player1Avatar}
                        alt={isPlayer2 ? player2Name : player1Name}
                      />
                      <AvatarFallback>{(isPlayer2 ? player2Name : player1Name)?.charAt(0)}</AvatarFallback>
                    </Avatar>
                  </div>
                </div>

                {/* Tabuleiro */}
                <div className="aspect-square w-full flex items-center justify-center px-0 md:px-0">
                  <div className="aspect-square w-full max-w-[min(100vw_-_12px,_560px)] md:max-w-[min(100vw_-_32px,_calc(75vh_-_32px))]">
                    <CheckerBoard
                      board={board}
                      onSquareClick={handleSquareClick}
                      selectedPiece={selectedPiece}
                      validMoves={validMoves}
                      currentPlayer={game.current_player}
                      gameStarted={gameStarted}
                      isPlayer2={isPlayer2}
                      mandatoryPieces={mandatoryPieces}
                      isMyTurn={isMyTurn}
                      isMobile={isMobile}
                      canJoinGame={canJoinGame}
                      onJoinGame={joinGame}
                      player1Id={game?.player1_id}
                      player2Id={game?.player2_id}
                    />
                  </div>
                </div>

                {/* Versão mobile do chat e histórico */}
                <div className="md:hidden w-full mt-3">
                  <Tabs defaultValue="chat" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 h-11 rounded-lg bg-zinc-950/70 border border-white/10 p-1">
                      <TabsTrigger
                        value="chat"
                        className="gap-2 rounded-md text-xs data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Chat
                      </TabsTrigger>
                      <TabsTrigger
                        value="history"
                        className="gap-2 rounded-md text-xs data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                      >
                        <ScrollText className="w-4 h-4" />
                        Histórico
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="chat" className="mt-2">
                      <div className="h-[270px] sm:h-[min(45dvh,400px)] overflow-hidden rounded-lg border border-white/10 bg-zinc-950/55">
                        {game && (
                          <GameChat
                            gameId={game.id}
                          moveHistory={moveHistory}
                          player1Name={player1Name}
                          player2Name={player2Name}
                          player1Id={game.player1_id}
                            player2Id={game.player2_id}
                          />
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="history" className="mt-2">
                      <div className="h-[270px] sm:h-[min(45dvh,400px)] overflow-hidden rounded-lg border border-white/10 bg-zinc-950/55">
                        <GameHistory
                          gameId={game.id}
                          moveHistory={moveHistory}
                        player1Name={player1Name}
                          player2Name={player2Name}
                        />
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>

              {/* Coluna da direita - Chat e Histórico (desktop) */}
              <div className="hidden md:flex w-[25%] max-w-[260px] min-w-[250px] flex-shrink-0 flex-col gap-4 h-[calc(75vh_-_32px)]">
                {/* Mensagem para usuários convidados */}
                {isGuest && (
                  <Card className="p-2 bg-blue-900/20 border border-blue-500/20">
                    <p className="text-zinc-300 text-sm">
                      Você está jogando no modo convidado. Para participar do ranking e ter acesso a skins personalizadas, faça login ou crie uma conta.
                    </p>
                  </Card>
                )}

                {/* Chat */}
                <Card className="flex-[1.8] bg-zinc-900/30 border-zinc-800/50 flex flex-col min-h-0">
                  <div className="p-2 border-b border-zinc-700/30">
                    <h3 className="font-semibold text-white/90 text-sm">Chat da Partida</h3>
                  </div>
                  <div className="flex-1 overflow-hidden min-w-0">
                    {game && (
                      <GameChat
                        gameId={game.id}
                        moveHistory={moveHistory}
                        player1Name={player1Name}
                        player2Name={player2Name}
                        player1Id={game.player1_id}
                        player2Id={game.player2_id}
                      />
                    )}
                  </div>
                </Card>

                {/* Histórico */}
                <Card className="flex-1 bg-zinc-900/30 border-zinc-800/50 flex flex-col min-h-0">
                  <div className="p-2 border-b border-zinc-700/30">
                    <h3 className="font-semibold text-white/90 text-sm">Histórico de Movimentos</h3>
                  </div>
                  <div className="flex-1 overflow-hidden min-w-0">
                    <GameHistory
                      gameId={game.id}
                      moveHistory={moveHistory}
                      player1Name={player1Name}
                      player2Name={player2Name}
                    />
                  </div>
                </Card>
              </div>
            </div>

            {/* Timer e outros elementos */}
            {/* {gameStarted && isMyTurn && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10">
                <GameTimer />
              </div>
            )} */}

            {/* Modal de vitória/derrota */}
            {(game.winner_id || player1Pieces === 0 || player2Pieces === 0) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-md z-50"
              >
                <motion.div
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  className="bg-zinc-900/80 backdrop-blur-md p-6 sm:p-8 rounded-xl border border-zinc-700/50 shadow-2xl max-w-md w-full mx-4"
                >
                  {(
                    // Caso de usuário logado vs logado
                    (!isGuest && game.winner_id && (
                      (isPlayer1 && game.winner_id === game.player1_id) ||
                      (isPlayer2 && game.winner_id === game.player2_id)
                    )) ||
                    // Caso de convidado vs convidado ou convidado vs logado
                    (isGuest && (
                      (isPlayer1 && player2Pieces === 0) ||
                      (isPlayer2 && player1Pieces === 0)
                    )) ||
                    // Caso especial: logado vs convidado
                    (!isGuest && !game.winner_id && (
                      (isPlayer1 && player2Pieces === 0) ||
                      (isPlayer2 && player1Pieces === 0)
                    ))
                  ) ? (
                    <div className="text-center space-y-4">
                      <motion.div
                        animate={{ rotate: [0, 10, -10, 0] }}
                        transition={{ duration: 0.5, repeat: 3 }}
                        className="text-6xl sm:text-7xl mb-4"
                      >
                        👑
                      </motion.div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-green-500">
                        Você Venceu!
                      </h2>
                      <p className="text-zinc-400">
                        Parabéns! Você é o grande campeão desta partida!
                      </p>
                    </div>
                  ) : (
                    <div className="text-center space-y-4">
                      <motion.div
                        animate={{ y: [0, -10, 0] }}
                        transition={{ duration: 0.5, repeat: 3 }}
                        className="text-6xl sm:text-7xl mb-4"
                      >
                        😔
                      </motion.div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-red-500">
                        Você Perdeu
                      </h2>
                      <p className="text-zinc-400">
                        Não foi dessa vez, mas continue tentando!
                      </p>
                    </div>
                  )}

                  <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      onClick={handlePlayAgain}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Jogar Novamente
                    </Button>
                    <Button
                      onClick={handleLeaveGame}
                      variant="outline"
                      className="bg-zinc-800 text-white hover:bg-zinc-700"
                    >
                      Voltar para Salas
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </Card>
        </div>
      </div>

      {/* Vinheta suave nas bordas */}
      <div className="absolute inset-0 pointer-events-none"
           style={{
             boxShadow: 'inset 0 0 200px rgba(0,24,69,0.7)'
           }}
      />

      {selectedPlayer && (
        <PlayerProfileCard
          userId={selectedPlayer}
          isOpen={true}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </div>
  );
};

export default GameRoom;
